import { Eye, EyeOff, Minus, Plus, Search, Upload, X } from 'lucide-react';
import { forwardRef, useId, useRef, useState, type ComponentType, type InputHTMLAttributes, type PointerEvent as ReactPointerEvent, type ReactNode, type TextareaHTMLAttributes } from 'react';
import { useT } from '@/lib/i18n';
import { cn } from '@/lib/utils';
import { mergeRefs, setNativeValue, useNativeControl } from './native';
import { Popover, PopoverPanel, PopoverTrigger } from './popover';
import { controlClass } from './styles';

type IconType = ComponentType<{ className?: string; 'aria-hidden'?: boolean }>;

export interface TextInputProps extends InputHTMLAttributes<HTMLInputElement> {
  invalid?: boolean;
  /** Drawn inside the field, on the left. */
  icon?: IconType;
  /** A unit or a currency, drawn inside the field on the right. */
  suffix?: ReactNode;
}

/** The plain text field, with room for a glyph and a unit inside the frame. */
export const TextInput = forwardRef<HTMLInputElement, TextInputProps>(function TextInput({ className, invalid, icon: Icon, suffix, ...props }, ref) {
  if (!Icon && !suffix) return <input ref={ref} className={cn(controlClass({ invalid }), 'px-3 placeholder:text-slate-400', className)} {...props} />;
  return (
    <div className={cn('relative w-full min-w-0', className)}>
      {Icon ? <Icon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" aria-hidden /> : null}
      <input ref={ref} className={cn(controlClass({ invalid }), 'px-3 placeholder:text-slate-400', Icon && 'pl-9', suffix && 'pr-10')} {...props} />
      {suffix ? <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-sm text-ink-muted">{suffix}</span> : null}
    </div>
  );
});

/** A search box: the glyph says what it is, the cross empties it in one click. */
export const SearchInput = forwardRef<HTMLInputElement, TextInputProps & { onClear?: () => void }>(function SearchInput({ onClear, className, invalid, ...props }, ref) {
  const t = useT();
  const own = useRef<HTMLInputElement | null>(null);
  const clearable = props.value !== undefined && String(props.value).length > 0;
  return (
    <div className={cn('relative w-full min-w-0', className)}>
      <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" aria-hidden />
      <input ref={mergeRefs(ref, own)} type="search" className={cn(controlClass({ invalid }), 'px-3 pl-9 pr-9 placeholder:text-slate-400 [&::-webkit-search-cancel-button]:hidden')} {...props} />
      {clearable ? (
        <button
          type="button"
          aria-label={t('control.clearSearch')}
          onClick={() => {
            if (own.current) setNativeValue(own.current, '');
            onClear?.();
          }}
          className="absolute right-2 top-1/2 -translate-y-1/2 cursor-pointer rounded-[6px] p-1 text-slate-400 transition hover:bg-slate-100 hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-300"
        >
          <X className="h-3.5 w-3.5" aria-hidden />
        </button>
      ) : null}
    </div>
  );
});

/** A password field that can show what was typed. */
export const PasswordInput = forwardRef<HTMLInputElement, TextInputProps>(function PasswordInput({ className, invalid, ...props }, ref) {
  const t = useT();
  const [visible, setVisible] = useState(false);
  return (
    <div className={cn('relative w-full min-w-0', className)}>
      <input ref={ref} type={visible ? 'text' : 'password'} className={cn(controlClass({ invalid }), 'px-3 pr-10 placeholder:text-slate-400')} {...props} />
      <button type="button" onClick={() => setVisible((v) => !v)} aria-label={t(visible ? 'control.hidePassword' : 'control.showPassword')} className="absolute right-2 top-1/2 -translate-y-1/2 cursor-pointer rounded-[6px] p-1.5 text-slate-400 transition hover:bg-slate-100 hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-300">
        {visible ? <EyeOff className="h-4 w-4" aria-hidden /> : <Eye className="h-4 w-4" aria-hidden />}
      </button>
    </div>
  );
});

/** A number field whose steppers are ours, not the browser's grey arrows. */
export const NumberInput = forwardRef<HTMLInputElement, TextInputProps>(function NumberInput({ className, invalid, suffix, disabled, ...props }, ref) {
  const t = useT();
  const own = useRef<HTMLInputElement | null>(null);

  function nudge(direction: 1 | -1) {
    const el = own.current;
    if (!el || el.disabled) return;
    if (direction > 0) el.stepUp();
    else el.stepDown();
    setNativeValue(el, el.value);
    el.focus();
  }

  return (
    <div className={cn('relative w-full min-w-0', className)}>
      <input ref={mergeRefs(ref, own)} type="number" disabled={disabled} className={cn(controlClass({ invalid }), 'numeric-plain px-3 pr-9 placeholder:text-slate-400')} {...props} />
      {suffix ? <span className="pointer-events-none absolute right-9 top-1/2 -translate-y-1/2 text-sm text-ink-muted">{suffix}</span> : null}
      <span className={cn('absolute right-1 top-1/2 flex -translate-y-1/2 flex-col', disabled && 'hidden')}>
        {[
          { dir: 1 as const, Icon: Plus, label: t('control.increase') },
          { dir: -1 as const, Icon: Minus, label: t('control.decrease') },
        ].map(({ dir, Icon, label }) => (
          <button key={label} type="button" tabIndex={-1} aria-label={label} onClick={() => nudge(dir)} className="cursor-pointer rounded-[4px] px-1 py-px text-slate-400 transition hover:bg-slate-100 hover:text-ink">
            <Icon className="h-3 w-3" strokeWidth={3} aria-hidden />
          </button>
        ))}
      </span>
    </div>
  );
});

const SWATCHES = ['#111111', '#252a31', '#5f6771', '#dc2626', '#ea580c', '#f2c94c', '#16a34a', '#0d9488', '#2563eb', '#7c3aed', '#db2777', '#ffffff'];

function hexToHsv(hex: string): [number, number, number] {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex);
  if (!m) return [0, 0, 0];
  const n = parseInt(m[1]!, 16);
  const r = ((n >> 16) & 255) / 255;
  const g = ((n >> 8) & 255) / 255;
  const b = (n & 255) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const d = max - min;
  let h = 0;
  if (d !== 0) {
    if (max === r) h = ((g - b) / d) % 6;
    else if (max === g) h = (b - r) / d + 2;
    else h = (r - g) / d + 4;
  }
  return [(h * 60 + 360) % 360, max === 0 ? 0 : d / max, max];
}

function hsvToHex(h: number, s: number, v: number): string {
  const c = v * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = v - c;
  const [r, g, b] = h < 60 ? [c, x, 0] : h < 120 ? [x, c, 0] : h < 180 ? [0, c, x] : h < 240 ? [0, x, c] : h < 300 ? [x, 0, c] : [c, 0, x];
  const channel = (n: number) =>
    Math.round((n + m) * 255)
      .toString(16)
      .padStart(2, '0');
  return `#${channel(r)}${channel(g)}${channel(b)}`;
}

function DragArea({ className, onMove, children }: { className?: string; onMove: (x: number, y: number) => void; children?: ReactNode }) {
  function report(event: ReactPointerEvent<HTMLDivElement>) {
    const box = event.currentTarget.getBoundingClientRect();
    onMove(Math.min(1, Math.max(0, (event.clientX - box.left) / box.width)), Math.min(1, Math.max(0, (event.clientY - box.top) / box.height)));
  }
  return (
    <div
      className={cn('relative cursor-crosshair touch-none', className)}
      onPointerDown={(e) => {
        e.currentTarget.setPointerCapture(e.pointerId);
        report(e);
      }}
      onPointerMove={(e) => {
        if (e.currentTarget.hasPointerCapture(e.pointerId)) report(e);
      }}
    >
      {children}
    </div>
  );
}

/** A colour field with Bulk's own picker rather than the operating system's. */
export const ColorInput = forwardRef<HTMLInputElement, Omit<TextInputProps, 'type'>>(function ColorInput({ className, invalid, disabled, id, title, 'aria-label': ariaLabel, ...props }, ref) {
  const t = useT();
  const { ref: nativeRef, value, commit } = useNativeControl<HTMLInputElement>(ref);
  const [open, setOpen] = useState(false);
  const hex = /^#[0-9a-f]{6}$/i.test(value) ? value : '#000000';
  const [h, s, v] = hexToHsv(hex);

  return (
    <div className={cn('relative min-w-0', className)}>
      <input ref={nativeRef} type="color" disabled={disabled} tabIndex={-1} aria-hidden className="sr-only" {...props} />
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            id={id}
            title={title}
            aria-haspopup="dialog"
            aria-expanded={open}
            aria-label={ariaLabel}
            disabled={disabled}
            className={cn(controlClass({ invalid }), 'flex w-full cursor-pointer items-center gap-2 px-2', open && 'border-slate-400 ring-2 ring-slate-200')}
          >
            <span className="h-6 w-6 shrink-0 rounded-[6px] border border-black/10" style={{ backgroundColor: hex }} aria-hidden />
            <span className="truncate font-mono text-xs uppercase text-ink-muted">{hex}</span>
          </button>
        </PopoverTrigger>
        <PopoverPanel className="w-60 p-3">
          <DragArea className="h-32 w-full overflow-hidden rounded-[8px] border border-line" onMove={(x, y) => commit(hsvToHex(h, x, 1 - y))}>
            <div className="absolute inset-0" style={{ background: `linear-gradient(to right, #fff, ${hsvToHex(h, 1, 1)})` }} />
            <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, #000, transparent)' }} />
            <span className="pointer-events-none absolute h-3 w-3 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white shadow" style={{ left: `${s * 100}%`, top: `${(1 - v) * 100}%`, backgroundColor: hex }} />
          </DragArea>
          <DragArea className="mt-3 h-3 w-full rounded-full" onMove={(x) => commit(hsvToHex(x * 360, s || 1, v || 1))}>
            <div className="absolute inset-0 rounded-full" style={{ background: 'linear-gradient(to right, #f00, #ff0, #0f0, #0ff, #00f, #f0f, #f00)' }} />
            <span className="pointer-events-none absolute top-1/2 h-4 w-4 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white shadow" style={{ left: `${(h / 360) * 100}%`, backgroundColor: hsvToHex(h, 1, 1) }} />
          </DragArea>
          <div className="mt-3 grid grid-cols-6 gap-1.5">
            {SWATCHES.map((swatch) => (
              <button key={swatch} type="button" aria-label={swatch} onClick={() => commit(swatch)} className={cn('h-6 w-full cursor-pointer rounded-[6px] border transition hover:scale-110', swatch.toLowerCase() === hex.toLowerCase() ? 'border-ink ring-2 ring-slate-300' : 'border-black/10')} style={{ backgroundColor: swatch }} />
            ))}
          </div>
          <input
            value={hex}
            onChange={(e) => {
              const next = e.target.value.startsWith('#') ? e.target.value : `#${e.target.value}`;
              if (/^#[0-9a-f]{6}$/i.test(next)) commit(next.toLowerCase());
            }}
            maxLength={7}
            aria-label={t('control.hexColour')}
            className={cn(controlClass(), 'mt-3 px-3 font-mono text-xs uppercase')}
          />
        </PopoverPanel>
      </Popover>
    </div>
  );
});

/** A file field: our button and our filename, never the browser's grey slab. */
export const FileInput = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement> & { buttonLabel?: string; invalid?: boolean }>(function FileInput({ className, invalid, buttonLabel, disabled, onChange, ...props }, ref) {
  const t = useT();
  const own = useRef<HTMLInputElement | null>(null);
  const [name, setName] = useState('');
  const id = useId();
  return (
    <div className={cn('relative w-full min-w-0', className)}>
      <input
        ref={mergeRefs(ref, own)}
        id={id}
        type="file"
        disabled={disabled}
        className="sr-only"
        onChange={(e) => {
          setName(e.target.files?.[0]?.name ?? '');
          onChange?.(e);
        }}
        {...props}
      />
      <label htmlFor={id} className={cn(controlClass({ invalid }), 'flex items-center gap-2 px-1.5', disabled ? 'cursor-not-allowed bg-slate-50' : 'cursor-pointer')}>
        <span className={cn('inline-flex h-7 shrink-0 items-center gap-1.5 rounded-[7px] bg-slate-100 px-2.5 text-xs font-semibold text-ink', !disabled && 'hover:bg-slate-200')}>
          <Upload className="h-3.5 w-3.5" aria-hidden /> {buttonLabel ?? t('control.chooseFile')}
        </span>
        <span className={cn('min-w-0 flex-1 truncate text-sm', name ? 'text-ink' : 'text-slate-400')}>{name || t('control.noFile')}</span>
      </label>
    </div>
  );
});

export const TextareaInput = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement> & { invalid?: boolean }>(function TextareaInput({ className, invalid, ...props }, ref) {
  return <textarea ref={ref} className={cn(controlClass({ invalid, height: false }), 'min-h-24 px-3 py-2 leading-relaxed placeholder:text-slate-400', className)} {...props} />;
});
